import Box from "@mui/material/Box"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import Stack from "@mui/material/Stack"
import Tooltip from "@mui/material/Tooltip"
import { styled } from "@mui/material/styles"
import type { ReactNode } from "react"
import { match } from "ts-pattern"
import { AutoField, useField } from "@w3rone/json-schema-form"
import type {
  FormSchema,
  ObjectFieldSchema,
  FieldComponentProps,
} from "@w3rone/json-schema-form"
import { FormErrors } from "./FormErrors"
import Info from "@mui/icons-material/Info"

export const NestedField = ({ name }: FieldComponentProps) => {
  const { schema, errors, label, description } = useField(name)

  if (schema.type !== "object") throw new Error("Bad schema type")

  const columns = getColumns(schema)

  const hasOnlyHiddenFields = Object.values(schema.properties).every(
    (field) => field.options.widget === "hidden",
  )

  const wrapFields = (children: ReactNode) =>
    match(schema.options?.layout)
      .with("inline", () => <InlineWrapper>{children}</InlineWrapper>)
      .with("two-col", () => <TwoColWrapper>{children}</TwoColWrapper>)
      .with("four-col", () => <FourColWrapper>{children}</FourColWrapper>)
      .otherwise(() => (
        <DefaultWrapper columns={columns}>{children}</DefaultWrapper>
      ))

  const style = { display: hasOnlyHiddenFields ? "none" : undefined }

  if (isFormSchema(schema)) {
    return (
      <Stack spacing={4} sx={style}>
        <FormErrors errors={errors} />
        <Box sx={{ marginTop: 4 }}>
          {wrapFields(
            <>
              {Object.keys(schema.properties).map((fieldName) => {
                return (
                  <AutoField
                    key={fieldName}
                    name={name ? `${name}[${fieldName}]` : fieldName}
                    required={schema.required.includes(fieldName)}
                  />
                )
              })}
            </>,
          )}
        </Box>
      </Stack>
    )
  }

  const visibleProperties = Object.entries(schema.properties).filter(
    ([, schema]) => schema.options.widget !== "hidden",
  )

  const hiddenProperties = Object.entries(schema.properties).filter(
    ([, schema]) => schema.options.widget === "hidden",
  )

  return (
    <FormControl component="fieldset" sx={style} fullWidth={true}>
      {label ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FormLabel component="legend">{label}</FormLabel>
          {description ? (
            <Tooltip
              title={<div dangerouslySetInnerHTML={{ __html: description }} />}
            >
              <Info />
            </Tooltip>
          ) : null}
        </Box>
      ) : null}
      <FormErrors errors={errors} />
      <Box
        sx={{
          marginTop: label ? 1 : 0,
        }}
      >
        {wrapFields(
          <>
            {visibleProperties.map(([key], index) => (
              <Box
                key={key}
                sx={{
                  gridColumn:
                    index === visibleProperties.length - 1 && index % 2 === 0
                      ? "1 / -1"
                      : undefined,
                }}
              >
                <AutoField name={`${name}[${key}]`} />
              </Box>
            ))}
          </>,
        )}
      </Box>
      <div>
        {hiddenProperties.map(([key]) => (
          <AutoField key={key} name={`${name}[${key}]`} />
        ))}
      </div>
    </FormControl>
  )
}

const DefaultWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "columns",
})<{ columns?: number }>(({ columns, theme }) => ({
  display: "grid",
  gap: theme.spacing(4),
  gridTemplateColumns: columns
    ? `repeat(${columns}, minmax(0, 1fr))`
    : undefined,
}))

const TwoColWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("sm")]: {
    columnCount: 2,
    gap: theme.spacing(3),
    "& > * + *": {
      marginTop: theme.spacing(3) + "!important",
    },
  },
}))

const FourColWrapper = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up("sm")]: {
    columnCount: 4,
    gap: theme.spacing(4),
    "& > * + *": {
      marginTop: theme.spacing(4) + "!important",
    },
  },
}))

const InlineWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginTop: 0,
  "& > *": {
    flexBasis: "100%",
    flexShrink: 1,
    flexGrow: 1,
  },
}))

const isFormSchema = (
  schema: FormSchema | ObjectFieldSchema,
): schema is FormSchema => {
  return typeof (schema as FormSchema).$id === "string"
}

type SchemaWithColumns = (FormSchema | ObjectFieldSchema) & {
  options: {
    columns?: number
  }
}

const getColumns = (schema: FormSchema | ObjectFieldSchema) => {
  const columns = (schema as SchemaWithColumns).options.columns

  return typeof columns === "number" && Number.isInteger(columns) && columns > 0
    ? columns
    : 1
}
